import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, InputNumber } from 'rsuite';
import {
  RunningClubCaptainProfile,
  UpsertRunningClubCaptainProfile
} from '@/models/running-club-captain-profile.model';
import { upsertRunningClubCaptainProfile } from '@/services/running-club-captain-profile.service';
import FetchingSelect from '@/components/FetchingSelect';
import ImageUploader from '@/components/ImageUploader';
import Textarea from '@/components/Textarea';
import { getRunningClubsLookup } from '@/services/running-club.service';
import { getRunningClubMembersLookup } from '@/services/running-club-member.service';
import { ObjectStorageFileCategory } from '@/models/storage.model';

const modelSchema = Schema.Model<UpsertRunningClubCaptainProfile>({
  id: Schema.Types.NumberType(),
  runningClubId: Schema.Types.NumberType().isRequired('This field is required.'),
  userId: Schema.Types.NumberType(),
  name: Schema.Types.StringType().maxLength(120, 'Name cannot exceed 120 characters.'),
  title: Schema.Types.StringType().maxLength(60, 'Title cannot exceed 60 characters.'),
  description: Schema.Types.StringType(),
  displayOrder: Schema.Types.NumberType().min(0, 'Display order cannot be negative.'),
});

interface RunningClubCaptainProfileDrawerProps extends DrawerProps {
  value?: RunningClubCaptainProfile,
  runningClubId?: number,
};

const RunningClubCaptainProfileDrawer: React.FC<RunningClubCaptainProfileDrawerProps> = ({ value, runningClubId, open, onClose, ...rest }) => {
  const emptyModel: UpsertRunningClubCaptainProfile = {
    id: undefined,
    runningClubId: runningClubId,
    userId: undefined,
    imageUrl: undefined,
    name: undefined,
    title: undefined,
    description: undefined,
    displayOrder: 0,
  };

  const [formValue, setFormValue] = useState<UpsertRunningClubCaptainProfile>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    setFormValue(value
      ? {
        id: value.id,
        runningClubId: value.runningClubId ?? runningClubId,
        userId: value.userId,
        imageUrl: value.imageUrl,
        name: value.name,
        title: value.title,
        description: value.description,
        displayOrder: value.displayOrder,
      }
      : emptyModel);
  }, [value, runningClubId]);

  const handleDrawerClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setFormValue(emptyModel);
    setErrorModel(undefined);
    setLoading(false);
    if (onClose) {
      onClose(event);
    }
  };

  const handleSubmit = async event => {
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<RunningClubCaptainProfile> = await upsertRunningClubCaptainProfile(formValue);
      if (result.success) {
        handleDrawerClose(event);
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }
    finally {
      setLoading(false);
    }
  };

  const handleFormChange = (changed: Record<string, any>) => {
    setFormValue(pre => ({
      ...pre,
      ...changed,
      // A user from the previous club would fail validation on submit, so switching
      // clubs drops the assignee rather than carrying it over silently.
      userId: changed.runningClubId !== pre.runningClubId ? undefined : changed.userId,
    }));
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right"
      open={open}
      onClose={onClose}
      {...rest}
    >
      <Drawer.Header>
        <Drawer.Title>{value ? 'Edit' : 'Add a new'} Captain Profile</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">
            Submit
          </Button>
          <Button onClick={handleDrawerClose} appearance="subtle">
            Cancel
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form name='upsert-running-club-captain-profile' fluid ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={modelSchema}>
          <Form.Group>
            <Form.Label>Running Club</Form.Label>
            <Form.Control name="runningClubId" block
              accepter={FetchingSelect}
              fetchData={(text, page) => getRunningClubsLookup(text, page)}
              disabled={!!runningClubId}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>User</Form.Label>
            <Form.Control name="userId" block
              accepter={FetchingSelect}
              fetchData={(text, page) => getRunningClubMembersLookup(text, formValue.runningClubId, page)}
              disabled={!formValue.runningClubId}
              cleanable
            />
            <Form.HelpText>Optional. Lists active members of this club only. Leave empty for a captain with no account yet.</Form.HelpText>
          </Form.Group>
          <Form.Group>
            <Form.Label>Picture</Form.Label>
            <Form.Control
              accepter={ImageUploader}
              name="imageUrl"
              height="260px"
              url={formValue.imageUrl}
              accept=".jpg,.jpeg,.png"
              objectStorageCategory={ObjectStorageFileCategory.RunningClub}
              onChange={url => setFormValue(pre => ({ ...pre, imageUrl: url }))}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control name="name" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Title</Form.Label>
            <Form.Control name="title" />
            <Form.HelpText>Shown under the name, for example Head Coach.</Form.HelpText>
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control name="description" accepter={Textarea} rows={5} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Display Order</Form.Label>
            <Form.Control name="displayOrder" accepter={InputNumber} min={0} />
            <Form.HelpText>Lower values are shown first.</Form.HelpText>
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }}></ErrorMessage>}
      </Drawer.Body>
    </Drawer>
  );
};

export default RunningClubCaptainProfileDrawer;
